import type { ProductImage } from "./products"
import PRODUCTS, { type ProductType, getPriceByProductId } from "./products"
import SERVICES, { type ServiceId, getPriceByServiceId } from "./services"
import TREATMENTS, { type TreatmentId, type TreatmentWeeks, type TreatmentProductRef, treatmentCatalogId } from "./treatments"
import type { PromotionView } from "./promotions"
import { toPromotionView } from "./promotions"
import { applyPercentDiscount, type ActivePromotionDTO } from "@biosany/shared"

export type ItemId = ProductType | ServiceId | TreatmentId

/** Nombre legible de cualquier ítem (producto, servicio o tratamiento). */
export const getItemName = (id: ItemId): string => {
	if (id in PRODUCTS) return PRODUCTS[id as ProductType].name
	if (id in SERVICES) return SERVICES[id as ServiceId].name
	return TREATMENTS[id as TreatmentId].name
}

/** Formatea un precio en pesos mexicanos, ej. `1200` → `"$1,200"`; con centavos si aplica. */
export const formatMXN = (amount: number): string => {
	const hasCents = Math.round(amount * 100) % 100 !== 0
	return `$${amount.toLocaleString("es-MX", {
		minimumFractionDigits: hasCents ? 2 : 0,
		maximumFractionDigits: hasCents ? 2 : 0
	})}`
}

/** Precio a mostrar, con descuento aplicado si hay una promoción activa. */
export interface DisplayPrice {
	/** Precio de lista, sin descuento. */
	original: number
	/** Precio final a pagar (con descuento si la promo está activa). */
	final: number
	/** `true` si hay una promoción activa que reduce el precio. */
	hasDiscount: boolean
	/** Etiqueta del descuento, ej. `"20%"`; vacío si no hay promo. */
	discountLabel: string
}

function itemIsPromoted(promo: PromotionView, itemId?: string): boolean {
	if (!promo.isEnabled || promo.discount <= 0) return false
	if (!itemId) return true
	return promo.itemIds.includes(itemId)
}

/**
 * Deriva el precio a mostrar a partir del precio de lista y la promoción activa.
 * Si `itemId` se indica, el descuento solo aplica si el ítem está en la promo.
 * Para tratamientos, pasa el id de catálogo por paquete (ver `getPromoMatchId`),
 * no el `TreatmentId` a secas — las promos aplican por paquete Básico/Completo.
 *
 * El descuento se calcula en centavos con `applyPercentDiscount` — la MISMA función que
 * usa el servidor para armar el cargo real de Stripe/PayPal (`computeLineDiscounts`).
 * Antes esto redondeaba a pesos enteros con `Math.round(price * offer)`, perdiendo los
 * centavos (ej. $69 con 10% mostraba $62 en vez de $62.10) y quedando desalineado del
 * monto que de verdad se cobraba — nunca reimplementes este redondeo aparte.
 */
export const getDisplayPrice = (price: number, promo?: PromotionView | null, itemId?: string): DisplayPrice => {
	const active = promo ?? toPromotionView(null)
	const applies = itemIsPromoted(active, itemId)
	const priceCents = Math.round(price * 100)
	const finalCents = applies ? applyPercentDiscount(priceCents, active.discountPercent) : priceCents
	return {
		original: price,
		final: finalCents / 100,
		hasDiscount: applies,
		discountLabel: applies ? active.percentage : ""
	}
}

export function promotionFromActive(active: ActivePromotionDTO | null): PromotionView {
	return toPromotionView(active)
}

/**
 * ID a comparar contra `promo.itemIds`. Los tratamientos se promocionan por
 * paquete (Básico/Completo) por separado, así que si `duration` viene informado
 * se usa el id de catálogo compuesto (`treatmentCatalogId`) en vez del
 * `TreatmentId` a secas.
 */
export function getPromoMatchId(item: { id: ItemId; duration?: TreatmentWeeks }): string {
	return item.duration ? treatmentCatalogId(item.id as TreatmentId, item.duration) : item.id
}

export const getTreatmentPrice = (products: TreatmentProductRef[]): number =>
	products.reduce((previous, current) => previous + getPriceById(current.id) * current.amount, 0)

export const getPriceById = (id: ItemId, duration?: TreatmentWeeks): number => {
	if (id in PRODUCTS) {
		return getPriceByProductId(id as ProductType)
	}
	if (id in SERVICES) {
		return getPriceByServiceId(id as ServiceId)
	}
	const option = TREATMENTS[id as TreatmentId].duration[duration ?? "two"]
	return getTreatmentPrice(option.products)
}

/** Display info for a treatment package at a given duration. */
export interface TreatmentItem {
	name: string
	set: string
	price: number
	priceId: string
	image: ProductImage
	imageCheckout: ProductImage
}

export const getTreatmentItem = (id: TreatmentId, duration: TreatmentWeeks): TreatmentItem => {
	const treatment = TREATMENTS[id]
	const option = treatment.duration[duration]
	const name = `Tratamiento para ${treatment.as} ${treatment.name.toLowerCase()}`

	return {
		name,
		set: `Duración: ${duration === "two" ? 2 : 4} semanas`,
		price: getTreatmentPrice(option.products),
		priceId: option.priceId,
		image: { source: option.image, width: 600, height: 800, alt: name },
		imageCheckout: { source: option.image, width: 150, height: 200, alt: name }
	}
}

export const getItemHref = (id: ItemId): string => {
	if (id in PRODUCTS) {
		return `/productos/${id}`
	}
	if (id in TREATMENTS) {
		return `/tratamientos/${id}`
	}
	return "/checkout"
}
